import React, { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toggleReaction, fetchReaction } from "../lib/api";
import { getSessionId } from "../lib/session";

/**
 * Like / dislike buttons with counts.
 * - `compact` variant fits inside dossier cards (no fetch on mount, uses passed initialCounts)
 * - default variant is for detail pages, fetches the user's own reaction state
 */
export default function ReactionButtons({
  targetType,
  targetId,
  compact = false,
  initialCounts = null, // {likes, dislikes} — for compact cards
  initialMine = null, // 'like' | 'dislike' | null — for compact cards
  stopPropagation = true,
}) {
  const [counts, setCounts] = useState(
    initialCounts || { likes: 0, dislikes: 0 }
  );
  const [mine, setMine] = useState(initialMine); // 'like' | 'dislike' | null
  const [busy, setBusy] = useState(false);

  // On non-compact (detail pages), fetch the session's existing reaction
  useEffect(() => {
    if (compact) return;
    const sid = getSessionId();
    fetchReaction(targetType, targetId, sid)
      .then((r) => {
        setCounts({ likes: r.likes || 0, dislikes: r.dislikes || 0 });
        setMine(r.mine || null);
      })
      .catch(() => {});
  }, [compact, targetType, targetId]);

  // Sync from props when they update (e.g., after bulk fetch on archive)
  useEffect(() => {
    if (compact && initialCounts) {
      setCounts(initialCounts);
    }
  }, [compact, initialCounts]);

  useEffect(() => {
    if (compact) {
      setMine(initialMine);
    }
  }, [compact, initialMine]);

  const onClick = async (e, reaction) => {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (busy) return;
    setBusy(true);
    const sid = getSessionId();
    try {
      const r = await toggleReaction(targetType, targetId, sid, reaction);
      setCounts({ likes: r.likes || 0, dislikes: r.dislikes || 0 });
      setMine(r.mine || null);
    } catch {
      // silent
    } finally {
      setBusy(false);
    }
  };

  const compactCls =
    "inline-flex items-center gap-1 font-mono text-[10px] tracking-wider transition-colors";
  const fullCls =
    "inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase transition-colors";

  if (compact) {
    return (
      <span className="flex items-center gap-3" data-testid={`reactions-${targetType}-${targetId}`}>
        <button
          type="button"
          onClick={(e) => onClick(e, "like")}
          disabled={busy}
          data-testid={`like-btn-${targetId}`}
          className={`${compactCls} ${mine === "like" ? "text-[#00ff88]" : "text-zinc-500 hover:text-[#00ff88]"}`}
          aria-label="Like"
          title="Like"
        >
          <ThumbsUp size={11} fill={mine === "like" ? "#00ff88" : "none"} />
          {counts.likes || 0}
        </button>
        <button
          type="button"
          onClick={(e) => onClick(e, "dislike")}
          disabled={busy}
          data-testid={`dislike-btn-${targetId}`}
          className={`${compactCls} ${mine === "dislike" ? "text-[#ff3b30]" : "text-zinc-500 hover:text-[#ff3b30]"}`}
          aria-label="Dislike"
          title="Dislike"
        >
          <ThumbsDown size={11} fill={mine === "dislike" ? "#ff3b30" : "none"} />
          {counts.dislikes || 0}
        </button>
      </span>
    );
  }

  return (
    <div
      className="flex items-center gap-2 no-print"
      data-testid={`reactions-${targetType}-${targetId}`}
    >
      <button
        type="button"
        onClick={(e) => onClick(e, "like")}
        disabled={busy}
        data-testid="like-btn"
        className={`${fullCls} ${
          mine === "like"
            ? "border-[#00ff88] text-[#00ff88] bg-[#00ff88]/10"
            : "border-white/15 text-zinc-400 hover:text-[#00ff88] hover:border-[#00ff88]/50"
        }`}
      >
        <ThumbsUp size={12} fill={mine === "like" ? "#00ff88" : "none"} />
        Like · {counts.likes || 0}
      </button>
      <button
        type="button"
        onClick={(e) => onClick(e, "dislike")}
        disabled={busy}
        data-testid="dislike-btn"
        className={`${fullCls} ${
          mine === "dislike"
            ? "border-[#ff3b30] text-[#ff3b30] bg-[#ff3b30]/10"
            : "border-white/15 text-zinc-400 hover:text-[#ff3b30] hover:border-[#ff3b30]/50"
        }`}
      >
        <ThumbsDown size={12} fill={mine === "dislike" ? "#ff3b30" : "none"} />
        Dislike · {counts.dislikes || 0}
      </button>
    </div>
  );
}
